import { Component, OnInit, OnDestroy, Input, HostBinding, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { ChatApiService } from '../../shared-services/chat-api.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DynamicEditTableComponent } from '../../chart-components/dynamic-edit-table/dynamic-edit-table.component';
import { StreamingTextComponent } from '../streaming-text/streaming-text.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';



interface Message {
  text: string;
  sender: 'user' | 'agent';
  files?: File[];
  type?: 'text' | 'table' | 'option' | 'json' | 'dropdown';
  data?: any;
  filteredData?: any[];
  searchQuery?: string;
  formattedText?: string;
  rawJson?: string;
  copied?: boolean;
  loading?: boolean;
  completed?: boolean;
}

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, DynamicEditTableComponent, StreamingTextComponent, MatFormFieldModule, MatSelectModule, MatInputModule],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild(DynamicEditTableComponent) private dynamicEditTable?: DynamicEditTableComponent;
  @ViewChild('chatBody') private chatBody!: ElementRef;
  @ViewChild('scrollAnchor') private scrollAnchor!: ElementRef;

  messages: Message[] = [];
  currentMessage = '';
  isLoading = false;
  selectedFiles: File[] = [];
  isDarkTheme = false;
  private sessionId: string | undefined;

  private subscriptions = new Subscription();
  private previousMessageCount = 0;

  constructor(
    private chatApiService: ChatApiService
  ){}

  ngOnInit(): void {
    // if (this.messages.length === 0) {
    //   this.messages.push({ text: 'Hello! How can I help you today?', sender: 'agent' });
    // }
    this.chatApiService.createSession().then(observable => {
      observable.subscribe({
        next: response => console.log('Session created:', response),
        error: error => console.error('Error creating session:', error)
      });
    });
  }

  ngAfterViewChecked() {
    if (this.messages.length !== this.previousMessageCount) {
      this.previousMessageCount = this.messages.length;
      this.scrollToBottom();
    }
  }

  toggleTheme(): void {
  this.isDarkTheme = !this.isDarkTheme;

  if (this.isDarkTheme) {
    document.body.classList.add('dark-theme');
  } else {
    document.body.classList.remove('dark-theme');
  }
}


  scrollToBottom(): void {
    try {
      setTimeout(() => {
        this.scrollAnchor.nativeElement.scrollIntoView({ behavior: 'auto', block: 'end' });
      }, 0);
    } catch (err) {
      console.error('Error in scrollToBottom:', err);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  startNewSession(): void {
    this.messages = [];
    this.sessionId = undefined;
    // this.messages.push({ text: 'Hello! How can I help you today?', sender: 'agent' });
    this.chatApiService.createSession().then(observable => {
      observable.subscribe({
        next: response => console.log('New session created:', response),
        error: error => console.error('Error creating new session:', error)
      });
    });
  }

  sendMessageOnEnter(event: Event | KeyboardEvent): void {
    const keyboardEvent = event as KeyboardEvent; // cast to KeyboardEvent
    if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
      keyboardEvent.preventDefault();
      this.sendMessage();
    }
  }

  formatText(text: string): string {
    let formatted = text;
    // bold
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong></strong>');
    // italic
    formatted = formatted.replace(/\*(.*?)\*/g, '<em></em>');

    // Convert hyphenated lists to HTML unordered lists
    // First, convert each hyphenated line to an <li> tag
    // This handles cases where a list starts on a new line or at the beginning of the text
    formatted = formatted.replace(/^-\s(.+)$/gm, '<li></li>');

    // Then, wrap consecutive <li> tags in a <ul> tag
    // The regex ensures that <ul></ul> wraps only blocks of <li> elements
    formatted = formatted.replace(/((?:<li>.*?<\/li>\s*)+)/g, '<ul></ul>');

    return formatted;
  }

  sendMessage(): void {
    if ((!this.currentMessage.trim() && this.selectedFiles.length === 0) || this.isLoading) {
      return;
    }

    let tableData: any[] | undefined;
    if (this.dynamicEditTable && this.dynamicEditTable.isTableEditable()) {
      tableData = this.dynamicEditTable.getTableData();

      // Find and remove the last message containing the table
      const tableMessageIndex = this.messages.map(m => m.type).lastIndexOf('table');
      if (tableMessageIndex > -1) {
        this.messages.splice(tableMessageIndex, 1);
      }
    }

    let jsonData: any | undefined;
    const jsonMessageIndex = this.messages.map(m => m.type).lastIndexOf('json');
    if (jsonMessageIndex > -1) {
      const jsonMessage = this.messages[jsonMessageIndex];
      try {
        if (jsonMessage.rawJson) {
          jsonData = JSON.parse(jsonMessage.rawJson);
        }
        this.messages.splice(jsonMessageIndex, 1);
      } catch (e) {
        console.error('Invalid JSON format:', e);
        // Optionally, show an error message to the user in the chat
        this.messages.push({ text: 'Error: Invalid JSON format. Please correct it before sending.', sender: 'agent' });
        return;
      }
    }

    const userMessage: Message = {
      text: this.currentMessage,
      sender: 'user',
      files: [...this.selectedFiles],
    };

    this.messages.push(userMessage);
    const messageToApi = this.currentMessage.trim();
    this.currentMessage = '';
    this.selectedFiles = [];
    this.isLoading = true;

    const turnStartIndex = this.messages.length;
    let accumulatedResponse = '';

   this.chatApiService.sendMessage(messageToApi, tableData, jsonData)
  .then(api$ => {
    const sub = api$.subscribe({
      next: (response: any) => {
        this.isLoading = false;

        // RESPONSE IS LIKE:
        // {
        //   message: "...",
        //   options: [...],
        //   session_id: "..."
        // }

        // if (response.message) {
        //   const separatorSymbol = '>>';

        //   if (response.message.startsWith(separatorSymbol)) {
        //     // --- Multi-bubble logic ---
        //     const cleanMessage = response.message.substring(separatorSymbol.length).trim();
        //     const messageParts = cleanMessage.split('\n').filter((part: string) => part.trim() !== '');

        //     messageParts.forEach((part: string, index: number) => {
        //       const message: Message = {
        //         text: part.trim(),
        //         sender: 'agent',
        //         formattedText: this.formatText(part.trim())
        //       };

        //       // Attach options to the last bubble
        //       if (index === messageParts.length - 1 && response.options) {
        //         if (response.ui_type === 'dropdown') {
        //           message.type = 'dropdown';
        //           message.data = response.options;
        //           message.filteredData = response.options;
        //         } else {
        //           message.type = 'option';
        //           message.data = response.options;
        //         }
        //       }
        //       this.messages.push(message);
        //     });

        //   } else {
        //     // --- Single-bubble logic (the default) ---
        //     const message: Message = {
        //       text: response.message,
        //       sender: 'agent',
        //       formattedText: this.formatText(response.message)
        //     };
        //     if (response.options) {
        //       if (response.ui_type === 'dropdown') {
        //         message.type = 'dropdown';
        //         message.data = response.options;
        //         message.filteredData = response.options;
        //       } else {
        //         message.type = 'option';
        //         message.data = response.options;
        //       }
        //     }
        //     this.messages.push(message);
        //   }
        // } 

        if (response.message) {
  const message: Message = {
    text: response.message,
    sender: 'agent',
    formattedText: this.formatText(response.message)
  };

  if (response.options) {
    if (response.ui_type === 'dropdown') {
      message.type = 'dropdown';
      message.data = response.options;
      message.filteredData = response.options;
    } else {
      message.type = 'option';
      message.data = response.options;
    }
  }

  this.messages.push(message);
}


      },
      error: (err: any) => {
        this.isLoading = false;
        this.messages.push({
          text: 'Something went wrong!',
          sender: 'agent'
        });
      }
    });

    this.subscriptions.add(sub);
  });

  }

  filterDropdownOptions(msg: Message, event: Event) {
    const searchTerm = (event.target as HTMLInputElement).value;
    if (!searchTerm) {
      msg.filteredData = msg.data;
      return;
    }
  
    msg.filteredData = msg.data.filter((option: any) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (fileList) {
      for (let i = 0; i < fileList.length; i++) {
        this.selectedFiles.push(fileList[i]);
      }
    }
  }

  removeFile(fileToRemove: File): void {
    this.selectedFiles = this.selectedFiles.filter(file => file !== fileToRemove);
  }

onOptionSelected(option: string): void {
  setTimeout(() => {
    this.currentMessage = option;

    const idx = this.messages.map(m => m.type).lastIndexOf('option') > -1 
                  ? this.messages.map(m => m.type).lastIndexOf('option') 
                  : this.messages.map(m => m.type).lastIndexOf('dropdown');

    if (idx !== -1) {
      const message = this.messages[idx];
      delete message.type;
      delete message.data;
      delete message.filteredData;
      delete message.searchQuery;
    }
  });
}



  copyJson(msg: Message): void {
    if (msg.rawJson) {
      navigator.clipboard.writeText(msg.rawJson).then(() => {
        msg.copied = true;
        setTimeout(() => {
          msg.copied = false;
        }, 2000); // Hide message after 2 seconds
      }).catch(err => {
        console.error('Failed to copy JSON: ', err);
      });
    }
  }
}